import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { message } from 'antd';
import BulkUpdateBanner from '../components/bulk-update-banner/BulkUpdateBanner';
import { apiService } from '../services/api';
import logger from '../utils/logger';

const BulkUpdateContext = createContext(null);

export const BULK_MODEM_UPDATE_MESSAGE_KEY = 'bulk-modem-update';

/**
 * Глобальное состояние массового обновления модемов (сетевые порты).
 * Не сбрасывается при смене раздела — баннер и опрос прогресса живут в провайдере.
 */
export const BulkUpdateProvider = ({ children }) => {
    const [bulkUpdateInProgress, setBulkUpdateInProgress] = useState(false);
    const [bulkUpdateBannerDismissed, setBulkUpdateBannerDismissed] = useState(false);
    const [bulkUpdateProgress, setBulkUpdateProgress] = useState(null);
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [completedBulkProgress, setCompletedBulkProgress] = useState(null);
    const intervalRef = useRef(null);

    const clearCompletedBulkProgress = useCallback(() => {
        setCompletedBulkProgress(null);
    }, []);

    const showBulkLoadingMessage = useCallback((content) => {
        message.loading({
            content,
            duration: 0,
            key: BULK_MODEM_UPDATE_MESSAGE_KEY,
        });
    }, []);

    const startBulkUpdate = useCallback(({ total = 0, phase = 'preparing' } = {}) => {
        setBulkUpdateInProgress(true);
        setBulkUpdateBannerDismissed(false);
        setBulkUpdateProgress({
            total: Math.max(0, Number(total) || 0),
            completed: 0,
            errors: 0,
            status: 'processing',
            phase,
            message: null,
        });
        const n = Math.max(0, Number(total) || 0);
        const label = n > 0
            ? `Применение настроек к ${n} модемам…`
            : 'Применение настроек модемов…';
        showBulkLoadingMessage(label);
    }, [showBulkLoadingMessage]);

    const stopBulkUpdate = useCallback(() => {
        setBulkUpdateInProgress(false);
        setActiveTaskId(null);
        setBulkUpdateProgress(null);
    }, []);

    const registerBulkUpdateTask = useCallback((taskId) => {
        if (!taskId) return;
        setActiveTaskId(taskId);
        setBulkUpdateProgress((prev) => ({
            ...(prev || {}),
            phase: 'kraken',
            status: 'processing',
        }));
    }, []);

    useEffect(() => {
        if (!activeTaskId) return undefined;

        const clearPolling = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const checkProgress = async (taskIdToCheck) => {
            try {
                const progressResponse = await apiService.getBulkUpdateProgress(taskIdToCheck);
                const progress = progressResponse.data;

                if (progress) {
                    const total = Number(progress.total) || 0;
                    const completed = Number(progress.completed) || 0;
                    const errors = Number(progress.errors) || 0;
                    setBulkUpdateProgress({
                        total,
                        completed,
                        errors,
                        status: progress.status,
                        phase: 'kraken',
                        message: progress.message || null,
                    });

                    if (progress.status === 'processing') {
                        const progressLabel = progress.message
                            || (total > 0
                                ? `Передача настроек в Kraken: ${completed} из ${total}…`
                                : 'Передача настроек в Kraken…');
                        showBulkLoadingMessage(progressLabel);
                    }
                }

                if (progress && progress.status === 'completed') {
                    clearPolling();
                    setActiveTaskId(null);
                    setBulkUpdateInProgress(false);
                    setBulkUpdateProgress(null);

                    const total = Number(progress.total) || 0;
                    const completed = Number(progress.completed) || 0;
                    const errors = Number(progress.errors) || 0;
                    const ok = Math.max(0, completed - errors);
                    const suffix = errors > 0 ? ` (ошибок: ${errors})` : '';
                    const doneText = total > 0
                        ? `Настройки применены: ${ok} из ${total} модемов${suffix}`
                        : `Массовое обновление модемов завершено${suffix}`;

                    message.success({
                        content: doneText,
                        key: BULK_MODEM_UPDATE_MESSAGE_KEY,
                        duration: 5,
                    });
                    setCompletedBulkProgress(progress);
                    return true;
                }
            } catch (error) {
                logger.warning('Ошибка получения прогресса:', error);
            }
            return false;
        };

        intervalRef.current = setInterval(() => {
            void checkProgress(activeTaskId);
        }, 1000);

        void checkProgress(activeTaskId);

        return () => {
            clearPolling();
        };
    }, [activeTaskId, showBulkLoadingMessage]);

    const value = {
        bulkUpdateInProgress,
        setBulkUpdateInProgress,
        bulkUpdateBannerDismissed,
        setBulkUpdateBannerDismissed,
        bulkUpdateProgress,
        completedBulkProgress,
        clearCompletedBulkProgress,
        registerBulkUpdateTask,
        startBulkUpdate,
        stopBulkUpdate,
        showBulkLoadingMessage,
    };

    return (
        <BulkUpdateContext.Provider value={value}>
            {children}
        </BulkUpdateContext.Provider>
    );
};

export const useBulkUpdate = () => {
    const ctx = useContext(BulkUpdateContext);
    if (!ctx) {
        throw new Error('useBulkUpdate must be used within BulkUpdateProvider');
    }
    return ctx;
};

/** Баннер над контентом маршрутов — не исчезает при смене раздела. */
export const BulkUpdateBannerHost = () => {
    const {
        bulkUpdateInProgress,
        bulkUpdateBannerDismissed,
        setBulkUpdateBannerDismissed,
        bulkUpdateProgress,
    } = useBulkUpdate();

    if (!bulkUpdateInProgress || bulkUpdateBannerDismissed) {
        return null;
    }

    return (
        <BulkUpdateBanner
            visible
            progress={bulkUpdateProgress}
            onClose={() => setBulkUpdateBannerDismissed(true)}
        />
    );
};
